import { useState, useRef, useEffect } from 'react';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import SandhillsReCAPTCHA from '@Components/Shared/SandhillsReCAPTCHA';
import { gtmPushEvent, sandhillsRequest, focusErroredField } from '@Components/Utilities/SandhillsUtility';
import TextField from '@Components/Shared/Controls/TextField';
import SimpleHomeBreadcrumb from '@Components/Shared/SimpleHomeBreadcrumb';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import SelectField from '@Components/Shared/Controls/SelectField';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import { SandhillsSlickSlider } from '@Components/Shared/SandhillsSlickSlider';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import '@Styles/vendors-extensions/_intl-tel-input.scss';
import ready from 'document-ready';

const PrimaryAdvertisingView = (props) => {

    const recaptchaRef = useRef(null);
    const [captchaOpen, setCaptchaOpen] = useState(false);
    const windowSize = useWindowSize();
    const [success, setSuccess] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [hideForm, setHideForm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [formData, setFormData] = useState({
        FirstName: props.FirstName || '',
        LastName: props.LastName || '',
        Email: props.Email || '',
        Phone: props.Phone || '',
        Company: props.Company || '',
        City: props.City || '',
        State: props.State || '',
        Country: '',
        PostalCode: props.PostalCode || '',
        TotalUsed: '',
        TotalNew: '',
        TotalRental: '',
        HowCanWeHelpYouItems: props.HowCanWeHelpYouItems || [],
        ModelType: props.ModelType
    });
    const inputReferences = useRef({});
    const routes = useRecoilValue(atoms.routes);
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const sitePhoneFormatCultureCode = useRecoilValue(atoms.sitePhoneFormatCultureCode);
    const currentUser = useRecoilValue(atoms.currentUser);
    const categoryName = (props.AlternateCategoryName && props.AlternateCategoryName.length > 0) ? props.AlternateCategoryName : props.DefaultCategoryName;

    useEffect(() => {

        ready(() => {
            if (!currentUser.IsAuthenticated) {
                const phoneElement = document.querySelector('#Phone');
                if (phoneElement) {
                    intlTelInput(phoneElement, {
                        autoHideDialCode: false,
                        autoPlaceholder: 'off',
                        nationalMode: false,
                        separateDialCode: true,
                        preferredCountries: ['us', 'gb', 'ca'],
                        initialCountry: sitePhoneFormatCultureCode ? sitePhoneFormatCultureCode.substring(sitePhoneFormatCultureCode.length - 2) : 'us'
                    });
                }
            }
        });
        setTimeout(() => {
            setValidationErrors({});
        }, 50);
    }, []);

    const submitForm = (e) => {
        e.preventDefault();
        let errors = run(formData, [
            ruleRunner('Email', 'Email', ValidationRules.requiredOr('Phone', 'Phone')),
            ruleRunner('Phone', 'Phone', ValidationRules.requiredOr('Email', 'Email')),
        ]);

        if (formData?.HowCanWeHelpYouItems?.filter(f => f.Selected)?.length <= 0) {
            errors['CheckboxError'] = 'Please select at least one checkbox option';
        }

        setFormData({
            ...formData,
            TotalUsed: formData.TotalUsed ? formData.TotalUsed : 0,
            TotalNew: formData.TotalNew ? formData.TotalNew : 0,
            TotalRental: formData.TotalRental ? formData.TotalRental : 0,
        });

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);
        setSubmitted(false);

        if (Object.keys(errors).length <= 0) {
            setSuccess(false);
            if (props.Captcha && recaptchaRef.current) {
                setCaptchaOpen(true);
                recaptchaRef.current.execute();
            } else {
                onSubmit();
            }
        }
    };

    const onSubmit = (token) => {
        setIsLoading(true);
        var dialCode = document.querySelector('.iti__selected-flag .iti__selected-dial-code')?.textContent || "";
        sandhillsRequest({
            url: routes.SubmitAdvertising,
            method: 'post',
            data: {
                ...formData,
                Phone: dialCode + ' ' + formData.Phone,
                Captcha: {
                    Response: token
                },
                webAgreementTypeIDs: props.WebAgreementTypeIDs
            }
        }).then((response) => {
            setIsLoading(false);
            if (response.Success) {
                gtmPushEvent('advertising-form');
            }

            setFormData({
                FirstName: response.FirstName || '',
                LastName: response.LastName || '',
                Email: response.Email || '',
                Phone: response.Phone || '',
                Company: response.Company || '',
                City: response.City || '',
                State: response.State || '',
                Country: '',
                PostalCode: response.PostalCode || '',
                TotalUsed: response.Success ? '' : response.TotalUsed.toString(),
                TotalNew: response.Success ? '' : response.TotalNew.toString(),
                TotalRental: response.Success ? '' : response.TotalRental.toString(),
                HowCanWeHelpYouItems: props.HowCanWeHelpYouItems || [],
                ModelType: response.ModelType
            });

            setSuccess(response.Success);
            setSubmitted(true);
            setValidationErrors(response.ValidationErrors || {});
            focusErroredField(response.ValidationErrors, inputReferences);

            if (response.UseLeadsUXUpdate && response.Success) {
                setHideForm(true);
                setTimeout(() => {
                    setHideForm(false);
                    setSuccess(false);
                    setSubmitted(false);
                }, 3000);
            }

            if (response.Captcha && recaptchaRef.current) {
                setCaptchaOpen(false);
                recaptchaRef.current.reset();
            }
        });
    };

    const onTextChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    };

    const onCheckboxChange = (e) => {
        let value = e.target.value;
        let checked = e.target.checked;
        setFormData(previousState => {
            return {
                ...previousState,
                HowCanWeHelpYouItems: [...previousState.HowCanWeHelpYouItems].map(item => {
                    if (item.Value == value) {
                        return { ...item, Selected: checked };
                    } else {
                        return { ...item };
                    }
                })
            };
        });
    };

    const pAdvertising = {
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5000,
        responsive: null,
        infinite: true
    };

    function getAdvertisingDescription() {
        let advertisingDescription = translator.translate([props.DescriptionTranslationKey]);
        advertisingDescription = advertisingDescription.replaceAll("{9}", currentSite.ProperDisplayDomainName);
        advertisingDescription = advertisingDescription.replaceAll("{8}", props.CountryName);
        advertisingDescription = advertisingDescription.replaceAll("{7}", currentSite.CurrentSite);
        advertisingDescription = advertisingDescription.replaceAll("{2}", props.FormattedPhoneNumber);
        advertisingDescription = advertisingDescription.replaceAll("{1}", props.ContactPhoneNumber);
        advertisingDescription = advertisingDescription.replaceAll("{0}", props.HasPublication ? currentSite.ProperDisplayDomainName.replace(currentSite.TopLevelDomain, '') : currentSite.ProperDisplayDomainName);
        return advertisingDescription;
    }

    return (
        <div className='advertising'>
            <div className='info-content-container'>
                <div className='info-content-header'>
                    <SimpleHomeBreadcrumb homeRoute={props.BreadCrumbHomeRoute} displayText={<TranslationText tKey='sandhills.tradepubwebsite.mvc.advertising' />} />
                </div>
                <div className='advertising-container'>
                    <h1 className='pagename border-bottom'>{currentSite.ProperDisplayDomainName == 'AuctionValues.com' ? translator.translate('sandhills.tradesites.mvc.auctionvaluesadvertisingtitle') : translator.translate('sandhills.tradesites.mvc.advertisingwithsite', [(props.HasPublication && !props.IsEuro) ? currentSite.ProperDisplayDomainName.replace(currentSite.TopLevelDomain, '') : currentSite.ProperDisplayDomainName])}</h1>
                    <div className='description-container border-bottom'>
                        <div className='advertising-description'>
                            <div dangerouslySetInnerHTML={{ __html: currentSite.ProperDisplayDomainName == 'AuctionValues.com' ? props.DescriptionTranslationKey : getAdvertisingDescription() }}></div><br />
                        </div>
                        {props.AdvertisingImageUrl &&
                            <div className='advertising-image'>
                                <img src={props.AdvertisingImageUrl} alt='Advertising' />
                            </div>}
                    </div>
                    {(props.SlideShowCards && props.SlideShowCards.length > 0) &&
                        <div className='slideshow'>
                            <ul className='cycle-slideshow'>
                                <SandhillsSlickSlider settings={pAdvertising} {...props}>
                                    {props.SlideShowCards.map((card, index) =>
                                        <li key={index}>
                                            <div className="Container">
                                                <div className="slide-text">
                                                    <h2 dangerouslySetInnerHTML={{ __html: card.ImageTitle }}></h2>
                                                    <p dangerouslySetInnerHTML={{ __html: card.ImageParagraph }}></p>
                                                </div>
                                                <div className="slide-image">
                                                    <img src={card.ImageUrl} alt={card.AltText} />
                                                </div>
                                            </div>
                                        </li>)}
                                </SandhillsSlickSlider>
                            </ul>
                        </div>}
                </div>
            </div>
            <div className='advertising-form'>
                <div className='info-content-container cf'>
                    <h2>{props.InterestedInAdvertisingHeading}</h2>
                    <h3 className='bottom-padding' dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.tradesites.mvc.pleasecallorfillouttheform', [props.ContactPhoneNumber, props.FormattedPhoneNumber]) }}></h3>
                    {props.UseLeadsUXUpdate && success &&
                        <div className='success' style={{ textAlign: 'center' }}>Successfully sent inquiry!</div>}
                    <form id='feedbackForm' className={(hideForm ? 'temp-gone' : '')} onSubmit={submitForm}>
                        <ValidationErrors errors={validationErrors} />
                        <div className='column7 left'>
                            <div className='column6 left'>
                                <TextField
                                    id='FirstName'
                                    name='FirstName'
                                    value={formData.FirstName}
                                    placeholder={translator.translate('sandhills.tradepubwebsite.mvc.firstname')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.firstname' />}
                                    class='modal-form-el'
                                    validationErrors={validationErrors.FirstName}
                                    ref={(element) => inputReferences.current[`FirstName`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='LastName'
                                    name='LastName'
                                    value={formData.LastName}
                                    placeholder={translator.translate('sandhills.tradepubwebsite.mvc.lastname')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.lastname' />}
                                    class='modal-form-el'
                                    validationErrors={validationErrors.LastName}
                                    ref={(element) => inputReferences.current[`LastName`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='Email'
                                    name='Email'
                                    value={formData.Email}
                                    placeholder={translator.translate('sandhills.email')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.email' />}
                                    class='modal-form-el'
                                    required={true}
                                    validationErrors={validationErrors.Email}
                                    ref={(element) => inputReferences.current[`Email`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='Phone'
                                    name='Phone'
                                    value={formData.Phone}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.phone' />}
                                    class='modal-form-el'
                                    required={true}
                                    validationErrors={validationErrors.Phone}
                                    ref={(element) => inputReferences.current[`Phone`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='Company'
                                    name='Company'
                                    value={formData.Company}
                                    placeholder={translator.translate('Sandhills.TradePubWebSite.MVC.Company')}
                                    type='text'
                                    label={<TranslationText tKey='Sandhills.TradePubWebSite.MVC.Company' />}
                                    class='modal-form-el'
                                    validationErrors={validationErrors.Company}
                                    ref={(element) => inputReferences.current[`Company`] = element}
                                    onFieldChanged={onTextChange}
                                />
                            </div>
                            <div className='column6 left'>
                                <TextField
                                    id='City'
                                    name='City'
                                    value={formData.City}
                                    placeholder={translator.translate('sandhills.city')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.city' />}
                                    class='modal-form-el'
                                    validationErrors={validationErrors.City}
                                    ref={(element) => inputReferences.current[`City`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                {props.UseTextFieldForState ? 
                                    <TextField
                                        id='State'
                                        name='State'
                                        value={formData.State}
                                        placeholder={translator.translate('sandhills.stateprovince')}
                                        type='text'
                                        label={<TranslationText tKey='sandhills.stateprovince' />}
                                        class='modal-form-el'
                                        validationErrors={validationErrors.State}
                                        ref={(element) => inputReferences.current[`State`] = element}
                                        onFieldChanged={onTextChange}
                                    />
                                    :
                                    props.GetStateDropDown && props.GetStateDropDown.length > 0 &&
                                    <SelectField
                                        id='State'
                                        name='State'
                                        value={formData.State}
                                        options={props.GetStateDropDown}
                                        label={<TranslationText tKey='sandhills.stateprovince' />}
                                        class='modal-form-el'
                                        selectClass='select-dropdown'
                                        validationErrors={validationErrors.State}
                                        ref={(element) => inputReferences.current[`State`] = element}
                                        onFieldChanged={onTextChange}
                                    />}
                                <TextField
                                    id='Country'
                                    name='Country'
                                    value={formData.Country}
                                    placeholder={translator.translate('sandhills.country')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.country' />}
                                    class='modal-form-el hidden'
                                    validationErrors={validationErrors.Country}
                                    ref={(element) => inputReferences.current[`Country`] = element}
                                    onFieldChanged={onTextChange}
                                    htmlAutoComplete='chrome-off'
                                />
                                <TextField
                                    id='PostalCode'
                                    name='PostalCode'
                                    value={formData.PostalCode}
                                    placeholder={translator.translate('sandhills.postalcode')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.postalcode' />}
                                    class='modal-form-el'
                                    validationErrors={validationErrors.PostalCode}
                                    ref={(element) => inputReferences.current[`PostalCode`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                {currentSite?.Site?.IsRentalYard ? 
                                    <TextField
                                        id='TotalRental'
                                        name='TotalRental'
                                        value={formData.TotalRental}
                                        placeholder={translator.translate('sandhills.tradesites.mvc.totalrental')}
                                        type='number'
                                        label={translator.translate('sandhills.tradesites.mvc.totalrental')}
                                        class='modal-form-el'
                                        inputClass='number-input'
                                        validationErrors={validationErrors.TotalRental}
                                        ref={(element) => inputReferences.current[`TotalRental`] = element}
                                        onFieldChanged={onTextChange}
                                    />
                                    :
                                    <>
                                        <TextField
                                            id='TotalUsed'
                                            name='TotalUsed'
                                            value={formData.TotalUsed}
                                            placeholder={translator.translate('sandhills.tradesites.mvc.totalusedcategory', [categoryName])}
                                            type='number'
                                            label={translator.translate('sandhills.tradesites.mvc.totalusedcategory', [categoryName])}
                                            class='modal-form-el'
                                            inputClass='number-input'
                                            validationErrors={validationErrors.TotalUsed}
                                            ref={(element) => inputReferences.current[`TotalUsed`] = element}
                                            onFieldChanged={onTextChange}
                                        />
                                        <TextField
                                            id='TotalNew'
                                            name='TotalNew'
                                            value={formData.TotalNew}
                                            placeholder={translator.translate('sandhills.tradesites.mvc.totalnewcategory', [categoryName])}
                                            type='number'
                                            label={translator.translate('sandhills.tradesites.mvc.totalnewcategory', [categoryName])}
                                            class='modal-form-el'
                                            inputClass='number-input'
                                            validationErrors={validationErrors.TotalNew}
                                            ref={(element) => inputReferences.current[`TotalNew`] = element}
                                            onFieldChanged={onTextChange}
                                        /></>}
                            </div>
                        </div>
                        <div className='column4 left checkbox-wrapper'>
                            <fieldset>
                                <legend className='ts__legend'><strong>{translator.translate('sandhills.tradesites.mvc.howcanwehelpyou')}</strong></legend>
                                {formData.HowCanWeHelpYouItems.map(item =>
                                    <div className='checkbox-container'>
                                        <input id={'HowCanWeHelpYouItems-' + item.Text} name={'HowCanWeHelpYouItems-' + item.Text} className='checkbox' value={item.Value} type='checkbox' checked={item.Selected} onChange={onCheckboxChange} />
                                        <label htmlFor={'HowCanWeHelpYouItems-' + item.Text}>{item.Text}</label>
                                    </div>
                                )}
                                <div className='error'>{validationErrors.CheckboxError}</div>
                            </fieldset>
                        </div>
                        <div className='clear' />
                        <div className='column12 left'>
                            {submitted &&
                                (success ?
                                    <div className='success'>Successfully sent inquiry!</div> :
                                    <div className='error'>An error occured, please try again.</div>)}
                        </div>
                        <div className='clear' />
                        <div className='column12 left'>
                            <div className='recaptcha-submit-container'>
                                <div className='captcha-placeholder'>
                                    {props.Captcha &&
                                        <SandhillsReCAPTCHA
                                            ref={recaptchaRef}
                                            sitekey={props.Captcha.Key}
                                            onChange={onSubmit}
                                            captchaOpen={captchaOpen}
                                        />}
                                </div>
                                <div dangerouslySetInnerHTML={{ __html: props.FeedbackPolicyNotice }} style={{ 'font-size': '.75em', 'margin-top': '10px' }} />
                                <input className='recaptcha-submit-button button ts-button' type='submit' id='advertising-submit-button' name='advertising-submit-button' value='Submit' />
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            <Backdrop
                open={isLoading}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress />
            </Backdrop>
        </div>
    );
};

export default PrimaryAdvertisingView;
global.PrimaryAdvertisingView = PrimaryAdvertisingView;